import { DateTime } from 'luxon';
import { capitalize } from 'vue';

import { areArraysIdentical, DASH } from '@/globals';
import i18n from '@/vueI18n';

export const DaysOfWeek = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

export const DaysOfWeekCapitalized = DaysOfWeek.map((x) => capitalize(x));

export const convertRangesToDaypart = (ranges) => {
  const week = [
    new Array(24).fill(0),
    new Array(24).fill(0),
    new Array(24).fill(0),
    new Array(24).fill(0),
    new Array(24).fill(0),
    new Array(24).fill(0),
    new Array(24).fill(0),
  ];

  for (const range of ranges) {
    for (let d = range.day; d <= (range.today || range.day); d += 1) {
      for (let h = range.from; h < Math.min(24, range.till || range.from); h += 1) {
        week[d][h] = 1;
      }
    }
  }
  return week.map((x) => x.join('')).join('');
};

export const daypartToRanges = (daypart) => {
  let groupStart = null;
  const ranges = [];
  const weekdays = [];

  const processRangeUpTo = (day) => {
    const hours = weekdays[day];
    if (hours.includes(1)) {
      let hourRangeStart = null;
      let lastHourOn = null;
      const hourRanges = hours.reduce((mem, h, i) => {
        if (h === 1) {
          if (lastHourOn == null) {
            hourRangeStart = i;
          }
          lastHourOn = i;
          if (i === hours.length - 1) {
            mem.push(
              hourRangeStart !== lastHourOn ? [hourRangeStart, lastHourOn + 1] : [hourRangeStart, hourRangeStart + 1],
            );
          }
        } else if (lastHourOn != null) {
          mem.push(
            hourRangeStart !== lastHourOn ? [hourRangeStart, lastHourOn + 1] : [hourRangeStart, hourRangeStart + 1],
          );
          hourRangeStart = null;
          lastHourOn = null;
        }
        return mem;
      }, []);

      for (let hri = 0; hri < hourRanges.length; hri += 1) {
        const hr = hourRanges[hri];
        let range = '';
        if (day === groupStart) {
          // same start and end day
          range = `${capitalize(DaysOfWeek[groupStart])}`;
        } else {
          range = `${capitalize(DaysOfWeek[groupStart])}-${capitalize(DaysOfWeek[day])}`;
        }
        if (hours.includes(0)) {
          range = `${range} ${hr.join('-')}`;
        }

        ranges.push(range);
      }
    }
  };
  for (let h = 0; h < daypart.length; h += 1) {
    if (h % 24 === 0) {
      weekdays[Math.floor(h / 24)] = [Number.parseInt(daypart[h], 10)];
    } else {
      weekdays[Math.floor(h / 24)].push(Number.parseInt(daypart[h], 10));
    }
  }
  for (let d = 0; d < weekdays.length; d += 1) {
    if (groupStart == null && weekdays[d].includes(1)) {
      groupStart = d;
    }

    if (d > 0) {
      // If the current day is different from the previous day,
      // process the range ending the previous day.
      if (!areArraysIdentical(weekdays[d - 1], weekdays[d])) {
        processRangeUpTo(d - 1);
        if (weekdays[d].includes(1)) {
          groupStart = d;
        } else {
          groupStart = null;
        }
      }
      // Always process ranges on Sunday
      if (d === weekdays.length - 1) {
        processRangeUpTo(d);
      }
    }
  }
  return ranges.join('; ');
};

export const preciseDaypartToRanges = (daypart) => {
  let groupStart = null;
  const ranges = [];
  const weekdays = [];
  const minutesPerDay = 60 * 24;

  const processRangeUpTo = (day) => {
    const minutes = weekdays[day];
    if (minutes.includes(1)) {
      let minuteRangeStart = null;
      let lastMinuteOn = null;
      const minuteRanges = minutes.reduce((mem, m, i) => {
        if (m === 1) {
          if (lastMinuteOn == null) {
            minuteRangeStart = i;
          }
          lastMinuteOn = i;
          if (i === minutes.length - 1) {
            mem.push(
              minuteRangeStart !== lastMinuteOn
                ? [minuteRangeStart, lastMinuteOn + 1]
                : [minuteRangeStart, minuteRangeStart + 1],
            );
          }
        } else if (lastMinuteOn != null) {
          mem.push(
            minuteRangeStart !== lastMinuteOn
              ? [minuteRangeStart, lastMinuteOn + 1]
              : [minuteRangeStart, minuteRangeStart + 1],
          );
          minuteRangeStart = null;
          lastMinuteOn = null;
        }
        return mem;
      }, []);

      for (let mri = 0; mri < minuteRanges.length; mri += 1) {
        const mr = minuteRanges[mri];
        let range = '';
        if (day === groupStart) {
          // same start and end day
          range = `${capitalize(DaysOfWeek[groupStart])}`;
        } else {
          range = `${capitalize(DaysOfWeek[groupStart])}-${capitalize(DaysOfWeek[day])}`;
        }
        if (minutes.includes(0)) {
          range = `${range} ${mr
            .map((x) =>
              DateTime.local()
                .startOf('day')
                .plus({ seconds: x * 60 })
                .toLocaleString(DateTime.TIME_SIMPLE),
            )
            .join('-')}`;
        }

        ranges.push(range);
      }
    }
  };
  for (let m = 0; m < daypart.length; m += 1) {
    if (m % minutesPerDay === 0) {
      weekdays[Math.floor(m / minutesPerDay)] = [Number.parseInt(daypart[m], 10)];
    } else {
      weekdays[Math.floor(m / minutesPerDay)].push(Number.parseInt(daypart[m], 10));
    }
  }
  for (let d = 0; d < weekdays.length; d += 1) {
    if (groupStart == null && weekdays[d].includes(1)) {
      groupStart = d;
    }

    if (d > 0) {
      // If the current day is different from the previous day,
      // process the range ending the previous day.
      if (!areArraysIdentical(weekdays[d - 1], weekdays[d])) {
        processRangeUpTo(d - 1);
        if (weekdays[d].includes(1)) {
          groupStart = d;
        } else {
          groupStart = null;
        }
      }
      // Always process ranges on Sunday
      if (d === weekdays.length - 1) {
        processRangeUpTo(d);
      }
    }
  }
  return ranges.join('; ');
};

export const formatRanges = (x) =>
  x == null
    ? DASH
    : x
        .map((range) => {
          let days = DaysOfWeekCapitalized[range.day];
          if (range.today != null) {
            days = `${days}-${DaysOfWeekCapitalized[range.today]}`;
          }
          let hours = `${range.from}`;
          if (range.till != null) {
            hours = `${hours}-${range.till}`;
          }
          return `${days} ${hours}`;
        })
        .join(', ');

const hourToString = (h) => DateTime.local().startOf('day').plus({ hours: h }).toLocaleString(DateTime.TIME_SIMPLE);

export const daypartToDailyRanges = (daypart) => {
  const ranges = [...DaysOfWeekCapitalized.map((x) => [x, []])];
  const weekdays = [];
  for (let h = 0; h < daypart.length; h += 1) {
    if (h % 24 === 0) {
      weekdays[Math.floor(h / 24)] = [Number.parseInt(daypart[h], 10)];
    } else {
      weekdays[Math.floor(h / 24)].push(Number.parseInt(daypart[h], 10));
    }
  }
  for (let dayIndex = 0; dayIndex < ranges.length; dayIndex += 1) {
    let previousHour = null;
    let startingHour = null;
    for (let hourIndex = 0; hourIndex < weekdays[dayIndex].length; hourIndex += 1) {
      const hour = weekdays[dayIndex][hourIndex];

      // Will decide where the starting hour of the period of time is.
      if ((hour === 1 && previousHour == null) || (previousHour === 0 && hour === 1)) {
        startingHour = hourIndex;
      }
      // IF - End of the period, push to array
      // ELSE IF - Reached the end of the file
      if (previousHour === 1 && hour === 0) {
        ranges[dayIndex][1].push(`${hourToString(startingHour)} – ${hourToString(hourIndex)}`);
      } else if (hourIndex === weekdays[dayIndex].length - 1 && hour === 1) {
        const newHourIndex = hourIndex + 1;
        ranges[dayIndex][1].push(`${hourToString(startingHour)} – ${hourToString(newHourIndex)}`);
      }

      previousHour = hour;
    }
  }
  return ranges;
};

export const parseRanges = (cell) => {
  const list = `${cell}`
    .split(';')
    .map((x) => x.trim())
    .filter((x) => x !== '');

  const normalizedRanges = [];

  const validationErrors = [];

  // Loop on all ranges
  list.forEach((range) => {
    // Regex match groups (not supported in Safari https://caniuse.com/js-regexp-lookbehind)
    const daysRegex = 'mon|tue|wed|thu|fri|sat|sun';
    const fromRegex = '(?:(?:2[0-3])|(?:[01]?[0-9]))(?::[0-5][0-9])?';
    const tillRegex = '(?:(?:2[0-4])|(?:[01]?[0-9]))(?::[0-5][0-9])?';

    const { day } = new RegExp('(?<day>daysRegex)'.replaceAll('daysRegex', daysRegex)).exec(
      range.toLowerCase(),
    )?.groups;
    const { today } = new RegExp('-(?<today>daysRegex)'.replaceAll('daysRegex', daysRegex)).exec(range.toLowerCase())
      ?.groups || { today: null };
    const { from, till } = new RegExp(
      '\\s+(?<from>fromRegex)-(?<till>tillRegex)'.replace('fromRegex', fromRegex).replace('tillRegex', tillRegex),
    ).exec(range.toLowerCase())?.groups || { from: null, till: null };
    // We need at least one weekday
    // no hours mean the entire day
    if (day == null) {
      validationErrors.push({
        value: range,
        error: i18n.global.t('messages.bulkLineItemsEditionValidationError_invalidDaypartRange'),
      });
      normalizedRanges.push('<error>');
    } else if (
      (today != null && DaysOfWeek.indexOf(today) < DaysOfWeek.indexOf(day)) ||
      (till != null && parseInt(till, 10) < parseInt(from, 10))
    ) {
      validationErrors.push({
        value: range,
        error: i18n.global.t('messages.bulkLineItemsEditionValidationError_invalidDaypartRange'),
      });
      normalizedRanges.push('<error>');
    } else {
      normalizedRanges.push({
        day: DaysOfWeek.indexOf(day),
        today: today != null ? DaysOfWeek.indexOf(today) : null,
        from: from !== null ? parseInt(from, 10) : 0,
        till: till !== null ? parseInt(till, 10) : 24,
      });
    }
  });

  return {
    validationErrors,
    ranges: normalizedRanges,
  };
};
